import { Box, Button, CircularProgress, Input, List, ListItem, ListItemText, Typography } from '@mui/material'
import React, { ChangeEvent, useState } from 'react'

import { functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';

interface ReindexVoterKeywordsProps {

}

const ReindexVoterKeywords: React.FC<ReindexVoterKeywordsProps> = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [taskCount, setTaskCount] = useState(0);
  const [failedKeywords, setFailedKeywords] = useState<string[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const queueTask = async (keywords: string[], retryCount = 0) => {
    const MAX_RETRIES = 3;
    const BASE_DELAY = 1000; // 1 second

    try {
      const refreshIndex = httpsCallable(functions, 'refresh_keyword_top_entries', { timeout: 1000 * 60 * 5 });
      const result = await refreshIndex({
        document: 'voter',
        field: 'name',
        keywords: keywords
      });
      console.log('Voter keywords re-indexed:', result);
    } catch (error) {
      if (retryCount < MAX_RETRIES) {
        // Exponential backoff
        const delay = BASE_DELAY * Math.pow(2, retryCount);
        console.warn(`Retry attempt ${retryCount + 1} for keywords ${keywords} after ${delay}ms`);
        
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, delay));
        
        // Recursive retry with increased retry count
        queueTask(keywords, retryCount + 1);
      } else {
        console.error(`Failed to reindex keywords ${keywords} after ${MAX_RETRIES} attempts:`, error);
        // Add keyword to failed keywords list
        setFailedKeywords(prev => [...prev, ...keywords]);
        throw error; // Rethrow to be caught by caller
      }
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setSelectedFile(file || null);
  };

  const refreshKeywordTopEntries = async () => {
    if (!selectedFile) {
      setError('Please select a keywords file first');
      return;
    }

    setIsLoading(true);
    setError(null);
    setTaskCount(0);
    setFailedKeywords([]); // Reset failed keywords

    try {
      // Read file contents
      const fileText = await selectedFile.text();
      
      // Split keywords, trim whitespace, remove empty lines
      const keywords = fileText
        .split(/\n/)
        .map(keyword => keyword.trim())
        .filter(keyword => keyword.length > 0);

      if (keywords.length === 0) {
        throw new Error('No keywords found in the uploaded file');
      }

      // Process keywords in batches
      const batchSize = 500;
      for (let i = 0; i < keywords.length; i += batchSize) {
        const batch = keywords.slice(i, i + batchSize);
        
        try {
          await queueTask(batch);
        } catch (batchError) {
          console.error(`Failed to process keywords batch:`, batchError);
        }

        // Update task count
        setTaskCount(i + batch.length);

        // Small delay between batches
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    } catch (err) {
      console.error('Error refreshing keyword top entries:', err);
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', p: 2 }}>
      <Typography variant="h6">Reindex Voter Keywords</Typography>
      
      <Input 
        type="file" 
        onChange={handleFileChange}
        inputProps={{ accept: '.txt' }}
        disabled={isLoading}
      />

      <Button 
        variant="contained" 
        color="primary" 
        onClick={refreshKeywordTopEntries}
        disabled={isLoading || !selectedFile}
        startIcon={isLoading ? <CircularProgress size={20} /> : null}
      >
        {isLoading ? 'Refreshing...' : 'Start'}
      </Button>

      {taskCount > 0 && (
        <Typography variant="body2" color="textSecondary">
          Tasks Queued: {taskCount}
        </Typography>
      )}

      {error && (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      )}

      {failedKeywords.length > 0 && (
        <Box sx={{ mt: 2, width: '100%', maxWidth: 400 }}>
          <Typography variant="subtitle1" color="error">Failed Keywords:</Typography>
          <List dense>
            {failedKeywords.map((keyword, index) => (
              <ListItem key={index}>
                <ListItemText primary={keyword} />
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Box>
  );
};

export default ReindexVoterKeywords;
